import { useMemo, useRef, useEffect } from 'react';
import { format, parseISO, isSameDay, isToday } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MapPin, Video, Clock, Users } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface CalendarEvent {
  id: string;
  subject: string;
  start_time: string;
  end_time: string;
  location?: string;
  teams_join_url?: string;
  is_all_day?: boolean;
  attendees?: any[];
}

interface CalendarDayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
}

const HOUR_HEIGHT = 60; // pixels per hour
const START_HOUR = 0;
const END_HOUR = 24;

function getEventPosition(event: CalendarEvent, currentDate: Date) {
  try {
    const start = parseISO(event.start_time);
    const end = parseISO(event.end_time);
    
    // Only show events that are on this day
    if (!isSameDay(start, currentDate) && !isSameDay(end, currentDate)) {
      return null;
    }
    
    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();
    const duration = Math.max(endMinutes - startMinutes, 30); // minimum 30 min
    
    const top = (startMinutes / 60) * HOUR_HEIGHT;
    const height = Math.max((duration / 60) * HOUR_HEIGHT, 28); // minimum 28px
    
    return { top, height, startMinutes, endMinutes };
  } catch {
    return null;
  }
}

function detectOverlaps(events: { event: CalendarEvent; position: any }[]) {
  const sorted = [...events].sort((a, b) => a.position.startMinutes - b.position.startMinutes);
  const columns: { event: CalendarEvent; position: any; column: number }[] = [];
  const columnEnds: number[] = [];
  
  for (const item of sorted) {
    let column = 0;
    for (let i = 0; i < columnEnds.length; i++) {
      if (columnEnds[i] <= item.position.startMinutes) {
        column = i;
        break;
      }
      column = i + 1;
    }
    
    columnEnds[column] = item.position.endMinutes;
    columns.push({ ...item, column });
  }
  
  const maxColumn = Math.max(...columns.map(c => c.column), 0) + 1;
  return columns.map(c => ({ ...c, totalColumns: maxColumn }));
}

export function CalendarDayView({ currentDate, events, onEventClick }: CalendarDayViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const showCurrentTime = isToday(currentDate);
  
  // Auto-scroll to current time or 8am
  useEffect(() => {
    if (scrollRef.current) {
      const scrollTo = showCurrentTime ? currentMinutes : 8 * 60;
      const scrollPosition = (scrollTo / 60) * HOUR_HEIGHT - 100;
      scrollRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [currentDate, showCurrentTime]);

  // Filter events for this day
  const dayEvents = useMemo(() => {
    const filtered = events
      .filter(event => {
        if (!event.start_time || !event.end_time) return false;
        try {
          const start = parseISO(event.start_time);
          return isSameDay(start, currentDate);
        } catch {
          return false;
        }
      })
      .map(event => ({
        event,
        position: getEventPosition(event, currentDate)
      }))
      .filter(item => item.position !== null);
    
    return detectOverlaps(filtered as any);
  }, [events, currentDate]);

  // All-day events
  const allDayEvents = useMemo(() => {
    return events.filter(event => {
      if (!event.start_time || event.is_all_day) {
        try {
          const start = parseISO(event.start_time);
          return isSameDay(start, currentDate);
        } catch {
          return false;
        }
      }
      return false;
    });
  }, [events, currentDate]);

  const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

  return (
    <div className="flex flex-col h-[600px] bg-card rounded-xl border border-border overflow-hidden">
      {/* Day header */}
      <div className="flex-shrink-0 border-b border-border bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={cn(
            "flex flex-col items-center justify-center w-14 h-14 rounded-xl",
            isToday(currentDate) 
              ? "bg-gradient-to-br from-energy-green to-energy-blue text-white" 
              : "bg-muted"
          )}>
            <span className="text-xs font-medium uppercase">
              {format(currentDate, 'EEE', { locale: nl })}
            </span>
            <span className="text-xl font-bold">
              {format(currentDate, 'd')}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {format(currentDate, 'EEEE d MMMM yyyy', { locale: nl })}
            </h3>
            <p className="text-sm text-muted-foreground">
              {dayEvents.length} afspra{dayEvents.length === 1 ? 'ak' : 'ken'}
            </p>
          </div>
        </div>
      </div>

      {/* All-day events */}
      {allDayEvents.length > 0 && (
        <div className="flex-shrink-0 border-b border-border bg-muted/20 px-4 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground font-medium">Hele dag:</span>
            {allDayEvents.map(event => (
              <div
                key={event.id}
                onClick={() => onEventClick?.(event)}
                className="px-3 py-1 rounded-full text-xs font-medium bg-energy-green/20 text-energy-green cursor-pointer hover:bg-energy-green/30 transition-colors"
              >
                {event.subject}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time grid */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="relative" style={{ height: (END_HOUR - START_HOUR) * HOUR_HEIGHT }}>
          {/* Hour lines */}
          {hours.map(hour => (
            <div
              key={hour}
              className="absolute left-0 right-0 border-t border-border/50"
              style={{ top: hour * HOUR_HEIGHT }}
            >
              <span className="absolute -top-3 left-2 text-xs text-muted-foreground font-mono bg-card px-1">
                {String(hour).padStart(2, '0')}:00
              </span>
            </div>
          ))}

          {/* Current time indicator */}
          {showCurrentTime && (
            <div
              className="absolute left-12 right-4 flex items-center z-20"
              style={{ top: (currentMinutes / 60) * HOUR_HEIGHT }}
            >
              <div className="w-3 h-3 rounded-full bg-energy-orange" />
              <div className="flex-1 h-0.5 bg-energy-orange" />
            </div>
          )}

          {/* Events */}
          <div className="absolute left-16 right-4 top-0 bottom-0">
            {dayEvents.map(({ event, position, column, totalColumns }) => {
              const width = `calc(${100 / totalColumns}% - 4px)`;
              const left = `calc(${(column / totalColumns) * 100}%)`;
              
              return (
                <div
                  key={event.id}
                  onClick={() => onEventClick?.(event)}
                  className={cn(
                    "absolute rounded-lg p-2 cursor-pointer overflow-hidden",
                    "bg-gradient-to-r from-energy-green/90 to-energy-green/70",
                    "hover:from-energy-green hover:to-energy-green/80",
                    "border-l-4 border-energy-green",
                    "shadow-sm hover:shadow-md transition-all duration-200",
                    "group"
                  )}
                  style={{
                    top: position.top,
                    height: position.height,
                    width,
                    left,
                  }}
                >
                  <div className="flex flex-col h-full text-white">
                    <div className="flex items-start justify-between gap-1">
                      <h4 className="font-semibold text-sm truncate flex-1">
                        {event.subject}
                      </h4>
                      {event.teams_join_url && (
                        <Video className="h-3.5 w-3.5 flex-shrink-0 text-energy-blue" />
                      )}
                    </div>
                    
                    {position.height > 40 && (
                      <div className="flex items-center gap-2 mt-1 text-xs text-white/80">
                        <Clock className="h-3 w-3" />
                        <span>
                          {format(parseISO(event.start_time), 'HH:mm')} - {format(parseISO(event.end_time), 'HH:mm')}
                        </span>
                      </div>
                    )}
                    
                    {position.height > 60 && event.location && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-white/70 truncate">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    )}
                    
                    {position.height > 80 && event.attendees && event.attendees.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-white/70">
                        <Users className="h-3 w-3" />
                        <span>{event.attendees.length} deelnemer{event.attendees.length > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
